import React from 'react';
import { Button, EmptyState, ErrorState, FilterChip, SearchInput } from '@parsina/framework';
import { Cpu, Plus, SearchX, X } from 'lucide-react';

import { useTranslation } from '../../i18n';
import { errorMessage, gql } from '../../lib/api';
import { useShortcut } from '../../lib/shortcuts';
import { modelsView } from '../../lib/aiPricingView';
import { SEARCH_DEBOUNCE_MS } from '../../constants/list';
import { MODELS_QUERY } from './queries';
import ModelsTable from './ModelsTable';
import ModelDialog from './ModelDialog';
import ModelDrawer from './ModelDrawer';
import PriceDialog from './PriceDialog';
import { CsvExportButton, TableSkeleton } from './shared';

/**
 * View «مدل‌ها» — Archetype B (list): the catalogue with its current price and
 * its usage in the window (design §12.4).
 *
 * The list arrives in one call; search, the deprecated toggle and the sort are
 * local (`modelsView`, pure and tested). `?model=<id>` opens the drawer, so a
 * deep link from the overview's breakdown lands on the model, not the list.
 */
export default function ModelsView({ window, view, go }) {
  const { t } = useTranslation();
  const searchRef = React.useRef(null);

  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [refreshing, setRefreshing] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [reloadKey, setReloadKey] = React.useState(0);

  const [adding, setAdding] = React.useState(false);
  const [editing, setEditing] = React.useState(null);
  const [pricing, setPricing] = React.useState(null);

  const includeDeprecated = view.deprecated === '1';

  const load = React.useCallback(({ quiet = false } = {}) => {
    if (quiet) setRefreshing(true); else setLoading(true);
    setError(null);
    gql(MODELS_QUERY, { window, includeDeprecated })
      .then((d) => setRows(d?.aiModels || []))
      .catch((e) => setError(errorMessage(e, t)))
      .finally(() => { setLoading(false); setRefreshing(false); });
  }, [window, includeDeprecated, t]);

  const first = React.useRef(true);
  React.useEffect(() => {
    load({ quiet: !first.current });
    first.current = false;
  }, [load]);

  const refresh = () => { load({ quiet: true }); setReloadKey((k) => k + 1); };

  const result = React.useMemo(() => modelsView(rows, view), [rows, view]);

  const [draft, setDraft] = React.useState(view.q || '');
  React.useEffect(() => { setDraft(view.q || ''); }, [view.q]);
  React.useEffect(() => {
    if (draft === (view.q || '')) return undefined;
    const timer = setTimeout(() => go({ q: draft }), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [draft, view.q, go]);

  const focusSearch = () => { searchRef.current?.focus(); searchRef.current?.select?.(); };
  useShortcut('/', focusSearch, { label: t('shortcuts.action.search'), group: t('shortcuts.groups.aiPricing') });

  const onSort = (key) => {
    const current = view.sort || 'cost';
    const dir = view.dir || 'desc';
    if (current !== key) go({ sort: key, dir: 'desc' });
    else if (dir === 'desc') go({ dir: 'asc' });
    else go({ sort: '', dir: '' });
  };

  const selected = view.model ? rows.find((r) => r.modelId === view.model) || null : null;

  const csvColumns = [
    { key: 'modelId', header: t('aiPricing.models.columns.model'), value: (r) => r.modelId },
    { key: 'provider', header: t('aiPricing.models.columns.provider'), value: (r) => r.provider },
    { key: 'status', header: t('aiPricing.models.columns.status'), value: (r) => r.status },
    { key: 'input', header: t('aiPricing.models.columns.input'), value: (r) => r.currentPrice?.inputUsdPerM },
    { key: 'output', header: t('aiPricing.models.columns.output'), value: (r) => r.currentPrice?.outputUsdPerM },
    { key: 'audio', header: t('aiPricing.models.columns.audio'), value: (r) => r.currentPrice?.audioUsdPerMinute ?? r.currentPrice?.audioInputUsdPerM },
    { key: 'since', header: t('aiPricing.models.columns.since'), value: (r) => r.currentPrice?.effectiveFrom },
    { key: 'calls', header: t('aiPricing.models.columns.calls'), value: (r) => r.callsInWindow },
    { key: 'cost', header: t('aiPricing.models.columns.cost'), value: (r) => r.costUsdInWindow },
  ];

  const countLabel = result.isFiltered
    ? t('aiPricing.models.countFiltered', { count: result.filtered, total: result.total })
    : t('aiPricing.models.count', { count: result.total });

  return (
    <div data-testid="ai-models">
      <div className="mb-2 flex flex-wrap items-center gap-3 rounded-lg border border-border bg-card p-3" data-testid="ai-models-toolbar">
        <div className="min-w-[16rem] flex-1">
          <SearchInput
            ref={searchRef}
            value={draft}
            onChange={(e) => setDraft(e.target?.value ?? e)}
            onKeyDown={(e) => { if (e.key === 'Escape' && draft) { e.stopPropagation(); setDraft(''); } }}
            placeholder={t('aiPricing.models.search')}
            aria-label={t('aiPricing.models.search')}
            data-testid="ai-models-search"
          />
        </div>
        <FilterChip
          label={t('aiPricing.models.showDeprecated')}
          active={includeDeprecated}
          onClick={() => go({ deprecated: includeDeprecated ? '' : '1' })}
          data-testid="ai-models-deprecated"
        />
        {result.isFiltered ? (
          <Button variant="ghost" size="sm" onClick={() => go({ q: '', deprecated: '' })} data-testid="ai-models-clear">
            <X size={14} aria-hidden />
            {t('common.actions.clearFilters')}
          </Button>
        ) : null}
        <CsvExportButton columns={csvColumns} rows={result.rows} data-testid="ai-models-csv" />
        <Button onClick={() => setAdding(true)} data-testid="ai-models-add">
          <Plus size={16} aria-hidden />
          {t('aiPricing.models.add')}
        </Button>
      </div>
      <p className="mb-3 text-xs text-muted-foreground" aria-live="polite" data-testid="ai-models-count">{countLabel}</p>

      <div className="rounded-lg border border-border bg-card transition-opacity" style={{ opacity: refreshing ? 0.45 : 1 }} aria-busy={refreshing || undefined}>
        {loading ? (
          <TableSkeleton cols={9} label={t('aiPricing.models.loading')} data-testid="ai-models-loading" />
        ) : error ? (
          <ErrorState
            title={t('aiPricing.models.error')}
            description={error}
            onRetry={load}
            retryLabel={t('common.actions.retry')}
            data-testid="ai-models-error"
          />
        ) : (
          <ModelsTable
            rows={result.rows}
            view={view}
            onSort={onSort}
            onOpen={(r) => go({ model: r.modelId })}
            onPrice={(r) => { go({ model: r.modelId }); setPricing(r); }}
            empty={result.isFiltered ? (
              <EmptyState
                icon={<SearchX size={44} strokeWidth={1.5} aria-hidden />}
                title={t('aiPricing.models.emptyFiltered.title')}
                description={t('aiPricing.models.emptyFiltered.desc')}
                action={<Button variant="outline" onClick={() => go({ q: '', deprecated: '' })}>{t('common.actions.clearFilters')}</Button>}
                data-testid="ai-models-empty"
              />
            ) : (
              <EmptyState
                icon={<Cpu size={44} strokeWidth={1.5} aria-hidden />}
                title={t('aiPricing.models.empty.title')}
                description={t('aiPricing.models.empty.desc')}
                action={<Button onClick={() => setAdding(true)}>{t('aiPricing.models.add')}</Button>}
                data-testid="ai-models-empty"
              />
            )}
          />
        )}
      </div>

      {view.model ? (
        <ModelDrawer
          model={selected}
          modelId={view.model}
          reloadKey={reloadKey}
          onClose={() => go({ model: '' })}
          onAddPrice={() => setPricing(selected || { modelId: view.model })}
          onEdit={() => setEditing(selected)}
        />
      ) : null}

      <ModelDialog
        open={adding || !!editing}
        onOpenChange={(open) => { if (!open) { setAdding(false); setEditing(null); } }}
        model={editing}
        onDone={refresh}
      />

      <PriceDialog
        open={!!pricing}
        onOpenChange={(open) => { if (!open) setPricing(null); }}
        model={pricing}
        onDone={refresh}
      />
    </div>
  );
}
